import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { withTheme } from 'styled-components';
import { publishFormat } from 'Workspace/Publications/utils/constants';
import { getActivityLiveDetails } from 'builder/utils';
import {
  List,
  ListBody,
  ListHeader,
  ListHeaderItem,
  ListItem,
  ListItemColumn,
  Spinner,
  SVGIcon,
} from '@pearsontechnology/aero-components';
import { intl } from 'containers/LanguageProvider/IntlGlobalProvider';
import publicationService from 'services/PublicationService';
import { getTime, sortByDate } from 'Workspace/Publications/utils';
import { timeFormats } from 'constants/constants';
import PublishStatusIcon from '../PublishStatusIcon';

import {
  ActivityName,
  CourseType,
  ColoredText,
  ProjectColumnText,
  CourseInfoWrapper,
} from './styles';
import { getColor, getIcon } from '../ProjectActivityList/uiHelpers';
import messages from './messages';

const outputTypesForDetails = [
  publishFormat.EPUB,
  publishFormat.PEARSON_PLUS,
];


const ProjectPublicationDetails = ({
  activityList,
  theme,
}) => {
  const [publicationDetails, setPublicationDetails] = useState([]);
  const nameColumnWidth = "60%";
  const defaultColumnWidth = "38%";

  const getPublicationDetails = async () => {
    const newPublicationDetails = [];
    for (const activity of activityList) {
      for (let outputTypeIx = 0; outputTypeIx < outputTypesForDetails.length; outputTypeIx++) { // Modern Physics[EPUB] and Modern Physics[Pearson+]
        const outputTypeLiveStatus = await getActivityLiveDetails(activity.activityId, outputTypesForDetails[outputTypeIx]); // Live status for EPUB and P+
        // TODO: Add market to the getPublicationHistory call
        const result = await publicationService.getPublicationHistory(activity.activityId, outputTypesForDetails[outputTypeIx]);
        const { publications = [] } = result || {};
        const sortedPublicationHistory = sortByDate(publications);
        for (let i = 0; i < sortedPublicationHistory?.length; i++) {
          const { etextVersion, updatedAt, author, description, version, bookId, title } = sortedPublicationHistory[i];
          if (etextVersion) {
            const key = activity.activityId + outputTypesForDetails[outputTypeIx];
            const publicationDetailEntry = {};
            const config = JSON.parse(description);
            const activityStatusInfo = { publicationDetails: { oculusStatus: outputTypeLiveStatus.pearsonPlusStatus } };
            publicationDetailEntry[key] = {
              activityDetailsForStatus: activityStatusInfo,
              etextVersion: etextVersion || '',
              publishDate: updatedAt || '',
              author: author || '',
              outputType: version || '',
              bookId: bookId || '',
              title: title || '',
              pluginType: activity.plugin.type,
              tocLevel: config?.tocLevel || '',
            };
            newPublicationDetails.push(publicationDetailEntry[key]);
            break;
          }
        }
      }
    }
    setPublicationDetails(newPublicationDetails);
  };

  useEffect(() => {
    getPublicationDetails();
  }, []);

  return (
    <List >
      <ListHeader >
        <ListItemColumn colWidth={nameColumnWidth} colWidthMobile={defaultColumnWidth}>
          <ListHeaderItem>{intl.formatMessage(messages.name)}</ListHeaderItem>
        </ListItemColumn>
        <ListItemColumn colWidth={defaultColumnWidth} colWidthMobile={defaultColumnWidth}>
          <ListHeaderItem>{intl.formatMessage(messages.author)}</ListHeaderItem>
        </ListItemColumn>
        <ListItemColumn colWidth={defaultColumnWidth} colWidthMobile={defaultColumnWidth}>
          <ListHeaderItem>{intl.formatMessage(messages.bookId)}</ListHeaderItem>
        </ListItemColumn>
        <ListItemColumn colWidth={defaultColumnWidth} colWidthMobile={defaultColumnWidth}>
          <ListHeaderItem>{intl.formatMessage(messages.version)}</ListHeaderItem>
        </ListItemColumn>
        <ListItemColumn colWidth={defaultColumnWidth} colWidthMobile={defaultColumnWidth}>
          <ListHeaderItem>{intl.formatMessage(messages.publishDate)}</ListHeaderItem>
        </ListItemColumn>
        <ListItemColumn colWidth={defaultColumnWidth} colWidthMobile={defaultColumnWidth}>
          <ListHeaderItem>{intl.formatMessage(messages.tocLevel)}</ListHeaderItem>
        </ListItemColumn>
      </ListHeader>
      <ListBody>
        {publicationDetails?.length === 0 && (
          <Spinner />
        )}
        {publicationDetails?.length > 0 && publicationDetails
          .map((pubDetail, ix) => (
            <ListItem
              handleKeyDownEvent={true}
              tabIndex={"0"}
              key={`pubdetail-item-${ix}`}
            >
              <ListItemColumn colWidth={nameColumnWidth} colWidthMobile={nameColumnWidth}>
                <ColoredText color={getColor(pubDetail.pluginType, theme)}>
                  <SVGIcon
                    icon={getIcon(pubDetail.pluginType)}
                    viewBox="0 0 24 24"
                    width={{ width: '24px', height: '24px' }}
                  />
                </ColoredText>
                <ActivityName>
                  {pubDetail.title || ''}
                  <CourseInfoWrapper>
                    <CourseType>{messages[pubDetail.outputType] ? intl.formatMessage(messages[pubDetail.outputType]) : ''}</CourseType>
                    <PublishStatusIcon activity={pubDetail.activityDetailsForStatus} theme={theme} />
                  </CourseInfoWrapper>
                </ActivityName>
              </ListItemColumn>
              <ListItemColumn colWidth={defaultColumnWidth} colWidthMobile={defaultColumnWidth}>
                <ProjectColumnText>{pubDetail?.author}</ProjectColumnText>
              </ListItemColumn>
              <ListItemColumn colWidth={defaultColumnWidth} colWidthMobile={defaultColumnWidth}>
                <ProjectColumnText>{pubDetail?.bookId}</ProjectColumnText>
              </ListItemColumn>
              <ListItemColumn colWidth={defaultColumnWidth} colWidthMobile={defaultColumnWidth}>
                <ProjectColumnText>{pubDetail?.etextVersion}</ProjectColumnText>
              </ListItemColumn>
              <ListItemColumn colWidth={defaultColumnWidth} colWidthMobile={defaultColumnWidth}>
                <ProjectColumnText>{getTime(pubDetail?.publishDate, timeFormats.DATE_MONTH)}</ProjectColumnText>
              </ListItemColumn>
              <ListItemColumn colWidth={defaultColumnWidth} colWidthMobile={defaultColumnWidth}>
                <ProjectColumnText>{pubDetail?.tocLevel}</ProjectColumnText>
              </ListItemColumn>
            </ListItem>
          ))}
      </ListBody>
    </List>
  );
};

ProjectPublicationDetails.propTypes = {
  activityList: PropTypes.array,
  theme: PropTypes.object,
};

export default withTheme(ProjectPublicationDetails);
